import { useAuth } from '@/context/AuthContext';

// Define a generic type for the service function
type ServiceFunction<T extends unknown[], R> = (...args: T) => Promise<R>;

export function useApiService() {
  const { signOut } = useAuth();

  const callService = async <T extends unknown[], R>(
    serviceFunction: ServiceFunction<T, R>,
    ...args: T
  ): Promise<R> => {
    try {
      return await serviceFunction(...args);
    } catch (error: any) { // Using 'any' for broader error property access
      console.error("API Service Error:", error);

      // Check for specific Supabase/PostgREST error codes for session issues
      // Common error messages for JWT issues from Supabase/PostgREST
      const commonAuthErrorMessages = [
        'JWT expired',
        'Invalid JWT',
        'jwk_not_found',
        'session_not_found'
      ];

      // Check if the error message includes any of the common auth error messages
      const messageBasedAuthError = typeof error.message === 'string' &&
                                    commonAuthErrorMessages.some(msg => error.message.toLowerCase().includes(msg.toLowerCase()));

      const isAuthError = error && (
        error.status === 401 || // Standard HTTP Unauthorized
        error.code === 'PGRST301' || // PostgREST specific: JWT validation error (often)
        error.code === 'session_not_found' || // Added this check for error code
        messageBasedAuthError
      );

      if (isAuthError) {
        console.log("Authentication error detected by useApiService. Signing out.");
        // Do not rethrow the error immediately after signOut if signOut causes navigation
        // as the component might unmount.
        try {
          await signOut();
          // Rethrow a generic error or a specific one if the UI needs to react beyond navigation
          // For now, we'll throw a new error to signal that the original operation failed due to auth
          throw new Error("Authentication failed. User has been signed out.");
        } catch (signOutError) {
          console.error("Error during signOut after API auth error:", signOutError);
          // If signOut itself fails, rethrow the original error or a new one
          throw error;
        }
      }
      // If not an auth error, or if signOut failed, rethrow the original error
      throw error;
    }
  };

  return { callService };
}
